import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import SearchOverlay from "@/components/feature/SearchOverlay";

export default function SiteHeader() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <div className="hidden border-b border-background-200 bg-background-100 lg:block">
        <div className="flex h-9 items-center justify-between px-6 text-xs text-foreground-500">
          <p className="flex items-center gap-2">
            <i className="ri-broadcast-line text-primary-500" />
            抖阳原创 · 每日更新文化、娱乐与生活深度内容
          </p>
          <p className="flex items-center gap-5">
            <span>2026 年 10 月 17 日 星期六</span>
            <span className="text-background-400">|</span>
            <span>内容为原创或已获授权，转载请注明来源</span>
          </p>
        </div>
      </div>

      <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur-md">
        <div className="flex h-16 items-center justify-between gap-4 px-4 md:h-[72px] md:px-6">
          <Link to="/" className="group flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 font-editorial text-lg font-bold text-background-50 transition-transform group-hover:rotate-6">
              抖
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-editorial text-xl font-bold tracking-wide text-foreground-950">抖阳</span>
              <span className="mt-1 font-label text-[10px] tracking-[0.28em] text-foreground-400">DUYANG</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            <Link
              to="/"
              className={`rounded-md px-3 py-2 text-sm transition-colors ${
                isActive("/") ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              首页
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setCategoryOpen(true)}
              onMouseLeave={() => setCategoryOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCategoryOpen((prev) => !prev)}
                className={`flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-sm transition-colors ${
                  location.pathname.startsWith("/category")
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
              </button>
              {categoryOpen ? (
                <div className="absolute left-0 top-full w-[420px] pt-2">
                  <div className="grid grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-50 p-2">
                    {categories.map((item) => (
                      <Link
                        key={item.id}
                        to={`/category/${item.id}`}
                        className="flex items-start gap-2 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100"
                      >
                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                          <i className={`${item.icon} text-sm`} />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                          <span className="mt-0.5 block truncate text-xs text-foreground-400">{item.slogan}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            {categories.slice(0, 3).map((item) => (
              <Link
                key={item.id}
                to={`/category/${item.id}`}
                className={`rounded-md px-3 py-2 text-sm transition-colors ${
                  location.pathname === `/category/${item.id}`
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`rounded-md px-3 py-2 text-sm transition-colors ${
                isActive("/about") ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              关于
            </Link>
            <Link
              to="/contact"
              className={`rounded-md px-3 py-2 text-sm transition-colors ${
                isActive("/contact") ? "text-primary-600" : "text-foreground-700 hover:text-primary-600"
              }`}
            >
              联系
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex h-9 cursor-pointer items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-500 transition-colors hover:border-primary-300 hover:text-foreground-800 md:w-56"
            >
              <i className="ri-search-line text-base" />
              <span className="hidden md:inline">搜索文章、视频</span>
              <span className="ml-auto hidden rounded border border-background-300 px-1.5 py-0.5 font-label text-[10px] text-foreground-400 md:inline">
                /
              </span>
            </button>
            <Link
              to="/contact"
              className="hidden cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-foreground-800 md:flex"
            >
              投稿与合作
              <i className="ri-arrow-right-up-line text-sm" />
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="打开菜单"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-lg`} />
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div className="animate-fade-in border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
            <div className="flex flex-col gap-1">
              <Link to="/" className="rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100">
                首页
              </Link>
              <p className="px-3 pb-1 pt-3 text-xs uppercase tracking-[0.18em] text-foreground-400">分类</p>
              {categories.map((item) => (
                <Link
                  key={item.id}
                  to={`/category/${item.id}`}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                >
                  <i className={`${item.icon} text-primary-500`} />
                  {item.name}
                  <span className="ml-auto text-xs text-foreground-400">{item.en}</span>
                </Link>
              ))}
              <p className="px-3 pb-1 pt-3 text-xs uppercase tracking-[0.18em] text-foreground-400">更多</p>
              <Link to="/about" className="rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100">
                关于我们
              </Link>
              <Link to="/contact" className="rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100">
                联系反馈
              </Link>
            </div>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                navigate("/contact");
              }}
              className="mt-3 flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md bg-primary-500 py-2.5 text-sm font-medium text-background-50"
            >
              投稿与合作
              <i className="ri-arrow-right-up-line" />
            </button>
          </div>
        ) : null}
      </header>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}